'use client';

import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Image from 'next/image';
import React, { useCallback, useEffect, useState } from 'react';
import { sectionHeadingTag } from '@/components/dynamic-zone/section-heading-level';
import { cn } from '@/lib/utils';
import { components } from '@/types/strapi';

export const ImageCarousel = ({
	images = [],
	heading,
	isFirst = false,
}: components['schemas']['DynamicZoneImageCarouselComponent'] & {
	isFirst?: boolean;
}) => {
	const SectionHeading = sectionHeadingTag(isFirst);
	// Return early if no images
	if (!images || images.length === 0) {
		return null;
	}

	const [emblaRef, emblaApi] = useEmblaCarousel({
		align: 'start',
		containScroll: false,
		dragFree: true,
		loop: true,
	});

	const [selectedIndex, setSelectedIndex] = useState(0);
	const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

	useEffect(() => {
		if (!emblaApi) return;

		setScrollSnaps(emblaApi.scrollSnapList());

		const onSelect = () => {
			setSelectedIndex(emblaApi.selectedScrollSnap());
		};

		emblaApi.on('select', onSelect);
		onSelect(); // Initial selection

		return () => {
			emblaApi.off('select', onSelect);
		};
	}, [emblaApi]);

	const scrollTo = useCallback(
		(index: number) => emblaApi && emblaApi.scrollTo(index),
		[emblaApi],
	);

	const scrollPrev = useCallback(() => {
		if (emblaApi) emblaApi.scrollPrev();
	}, [emblaApi]);

	const scrollNext = useCallback(() => {
		if (emblaApi) emblaApi.scrollNext();
	}, [emblaApi]);

	return (
		<div className="image-carousel-section mx-auto mb-4 space-y-4">
			<noscript>
				<style>{`
					.image-carousel-section .overflow-hidden {
						overflow: visible !important;
					}
					.image-carousel-section .flex {
						flex-wrap: wrap !important;
					}
					.image-carousel-section [class*='flex-[0_0'] {
						flex: 0 0 100% !important;
						max-width: 100% !important;
					}
					.image-carousel-section button {
						display: none !important;
					}
				`}</style>
			</noscript>
			{heading && (
				<SectionHeading className="text-center text-xl font-semibold text-gray-900 sm:text-2xl md:text-3xl">
					{heading}
				</SectionHeading>
			)}
			<div
				className={cn(
					'relative mx-auto w-[95%] max-w-full px-1 sm:w-full sm:px-4',
				)}>
				<div className="overflow-hidden" ref={emblaRef}>
					<div className="flex">
						{images.map((item, index) => (
							<div
								key={index}
								className={cn(
									'relative min-w-0 pl-1 sm:pl-4',
									images.length === 1
										? 'flex-[0_0_100%]'
										: images.length === 2
											? 'flex-[0_0_50%]'
											: 'flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_33.333%]',
								)}>
								<div className="relative aspect-[16/9] w-full overflow-hidden rounded-lg">
									{item.image && item.image.url && (
										<Image
											src={item.image.url}
											alt={
												item.image.alternativeText ||
												`Slide ${index + 1}`
											}
											fill
											className="object-cover"
											sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 95vw"
											priority={isFirst && index === 0}
											loading={
												isFirst && index === 0
													? 'eager'
													: 'lazy'
											}
										/>
									)}
								</div>
								{item.description && (
									<p className="mt-2 line-clamp-2 text-center text-xs text-gray-600 sm:text-sm md:text-base">
										{item.description}
									</p>
								)}
							</div>
						))}
					</div>
				</div>

				<button
					onClick={scrollPrev}
					className="absolute -left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-1.5 shadow-md transition-all hover:bg-white sm:left-2 sm:p-2"
					aria-label="Previous slide">
					<ChevronLeft className="h-4 w-4 sm:h-6 sm:w-6" />
				</button>

				<button
					onClick={scrollNext}
					className="absolute -right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-1.5 shadow-md transition-all hover:bg-white sm:right-2 sm:p-2"
					aria-label="Next slide">
					<ChevronRight className="h-4 w-4 sm:h-6 sm:w-6" />
				</button>

				<div className="mt-4 flex justify-center gap-2">
					{scrollSnaps.map((_, index) => (
						<button
							key={index}
							className={cn(
								'h-2 w-2 rounded-full transition-all sm:h-2.5 sm:w-2.5',
								selectedIndex === index
									? 'bg-gray-900'
									: 'bg-gray-300 hover:bg-gray-400',
							)}
							aria-label={`Go to slide ${index + 1}`}
							onClick={() => scrollTo(index)}
						/>
					))}
				</div>
			</div>
		</div>
	);
};
